import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AgreementPreview } from './AgreementPreview';
import { SignatureCapture } from './SignatureCapture';
import { getAgreementBySignatureId, submitSignatureById } from '../services/agreementService';
import { useAgreement } from '../context/AgreementContext';
import { AgreementContextData } from '../types';

export function SecondarySignerView() {
  const { signature_id } = useParams<{ signature_id: string }>();
  const navigate = useNavigate();
  const { dispatch } = useAgreement();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agreementId, setAgreementId] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [signatureData, setSignatureData] = useState<string>('');
  const [signerName, setSignerName] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [signatureSubmitted, setSignatureSubmitted] = useState<boolean>(false);

  useEffect(() => {
    const fetchAgreementData = async () => {
      if (!signature_id) {
        setError('No signature ID provided');
        setLoading(false);
        return;
      }

      console.log(`Fetching agreement data for signature ID: ${signature_id}`);
      setDebugInfo(`Signature ID: ${signature_id}`);

      try {
        setLoading(true);
        const data = await getAgreementBySignatureId(signature_id);
        console.log('Secondary signer view - Fetched agreement data:', data);
        
        if (data && data.id) {
          setAgreementId(data.id);
          console.log(`Agreement ID found: ${data.id}`);
          setDebugInfo(prev => `${prev}\nAgreement ID: ${data.id}`);
          
          // Check if the agreement has signatures
          if (data.signatures && Array.isArray(data.signatures)) {
            console.log(`Agreement has ${data.signatures.length} signatures`);
            setDebugInfo(prev => `${prev}\nSignatures: ${data.signatures.length}`);
            
            // Find the current signature in the signatures array
            const currentSignature = data.signatures.find(
              (sig: any) => sig.id === signature_id
            );
            
            if (currentSignature) {
              console.log(`Found current signature: ${JSON.stringify(currentSignature)}`);
              setDebugInfo(prev => `${prev}\nCurrent signature: ${JSON.stringify(currentSignature)}`);
              
              // Check if the signature has already been submitted
              if (currentSignature.signed_at) {
                setSignatureSubmitted(true);
                console.log('Signature has already been submitted, redirecting to success page');
                setDebugInfo(prev => `${prev}\nSignature already submitted at: ${currentSignature.signed_at}`);
                // Redirect to success page if signature is already completed
                navigate('/signature-success');
                return;
              }
            } else {
              console.log(`Current signature not found in signatures array`);
              setDebugInfo(prev => `${prev}\nCurrent signature not found in signatures array`);
            }
          } else {
            console.log(`Agreement has no signatures array`);
            setDebugInfo(prev => `${prev}\nNo signatures array found`);
          }
          
          // Try to get the signer name from purchaser details
          try {
            const purchaserDetails = data.purchaser_details;
            let foundName = '';
            
            if (purchaserDetails) {
              // Find the current signature in the signatures array to get its order
              const currentSignature = data.signatures?.find(
                (sig: any) => sig.id === signature_id
              );
              
              const signatureOrder = currentSignature?.order || -1;
              console.log(`Current signature order: ${signatureOrder}`);
              
              // Get the appropriate individuals array based on purchaser type
              let individuals = [];
              if (purchaserDetails.purchaserType === 'personal' && purchaserDetails.individualDetails) {
                individuals = purchaserDetails.individualDetails;
              } else if (purchaserDetails.purchaserType !== 'personal' && purchaserDetails.individualDetails) {
                // For entity types (company, trust, smsf), the directors are stored in individualDetails
                individuals = purchaserDetails.individualDetails;
              }
              
              // Try to find the individual based on signature order
              // Signature order is 1-based, array index is 0-based
              // Order 1 is primary signer, order 2+ are secondary signers
              if (signatureOrder > 1 && individuals.length >= signatureOrder) {
                const individual = individuals[signatureOrder - 1];
                if (individual && individual.firstName && individual.lastName) {
                  foundName = `${individual.firstName.trim()} ${individual.lastName.trim()}`;
                  console.log(`Found signer name: ${foundName}`);
                } else {
                  console.log('Individual found but missing name data:', individual);
                  foundName = `Signer ${signatureOrder}`;
                }
              }
              // Fallback: if we can't match by order, try to find any secondary signer
              else if (individuals.length > 1) {
                // For secondary signers, we're looking at individuals after the first one
                const individual = individuals[1]; // Take the second individual as a fallback
                if (individual && individual.firstName && individual.lastName) {
                  foundName = `${individual.firstName.trim()} ${individual.lastName.trim()}`;
                  console.log(`Fallback signer name: ${foundName}`);
                } else {
                  console.log('Fallback individual found but missing name data:', individual);
                  foundName = 'Secondary Signer';
                }
              } else {
                console.log('No suitable individuals found for secondary signer. Debug info:', {
                  signatureOrder: signatureOrder,
                  individualsLength: individuals.length,
                  purchaserType: purchaserDetails.purchaserType,
                  individuals: individuals.map((ind: any) => ({ firstName: ind.firstName, lastName: ind.lastName, email: ind.email }))
                });
                foundName = 'Secondary Signer';
              }
            }
            
            // Set the final name (use foundName or default)
            const finalName = foundName || 'Secondary Signer';
            setSignerName(finalName);
            console.log(`Setting final signer name: ${finalName}`);
          } catch (err) {
            console.error('Error getting signer name:', err);
            setSignerName('Secondary Signer');
          }
          
          // Load agreement data into context
          if (data.purchaser_details) {
            console.log('SecondarySignerView - Loading agreement data into context');
            
            // Create a data structure that matches what the LOAD_AGREEMENT action expects
            const loadData: AgreementContextData = {
              id: data.id,
              purchaserType: data.purchaser_details.purchaserType || 'personal',
              partySize: data.purchaser_details.individualDetails?.length || 1,
              preferences: data.purchaser_details.agreementDetails?.preferences || {
                maxPrice: 1000000,
                propertyPreferences: ''
              },
              individualDetails: data.purchaser_details.individualDetails || [],
              signatures: data.signatures || [],
              currentStep: 4, // Review & Sign step
              isComplete: data.is_draft === false,
              total_fee: data.total_fee,
              total_retainer: data.total_retainer
            };
            
            // Handle entity-specific details based on purchaser type
            if (data.purchaser_details.purchaserType === 'smsf' && data.purchaser_details.agreementDetails) {
              loadData.smsfDetails = {
                smsfName: data.purchaser_details.agreementDetails.smsfName || '',
                corporateTrustee: data.purchaser_details.agreementDetails.corporateTrustee || '',
                trustName: data.purchaser_details.agreementDetails.trustName || '',
                directors: data.purchaser_details.individualDetails || []
              };
            } else if (data.purchaser_details.purchaserType === 'trust' && data.purchaser_details.agreementDetails) {
              loadData.trustDetails = {
                trustName: data.purchaser_details.agreementDetails.trustName || '',
                directors: data.purchaser_details.individualDetails || []
              };
            } else if (data.purchaser_details.purchaserType === 'company' && data.purchaser_details.agreementDetails) {
              loadData.companyDetails = {
                trustName: data.purchaser_details.agreementDetails.trustName || '',
                corporateTrustee: data.purchaser_details.agreementDetails.corporateTrustee || '',
                directors: data.purchaser_details.individualDetails || []
              };
            }
            
            // Load the agreement data into the context
            dispatch({ type: 'LOAD_AGREEMENT', payload: loadData });
            console.log('SecondarySignerView - Agreement data loaded into context:', loadData);
          }
        } else {
          console.error('Invalid agreement data structure:', data);
          setDebugInfo(prev => `${prev}\nInvalid data structure: ${JSON.stringify(data)}`);
          setError('Invalid agreement data received');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        console.error('Error fetching agreement data:', errorMessage);
        setDebugInfo(prev => `${prev}\nError: ${errorMessage}`);
        setError('Failed to load agreement data. The signature link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };

    fetchAgreementData();
  }, [signature_id]);

  const handleBack = () => {
    // For secondary signers, there's no back navigation
    // This is just a placeholder function since AgreementPreview requires it
    console.log('Back button clicked in secondary signer view (no action)');
  };
  
  const handleSignatureChange = (signature: string) => {
    setSignatureData(signature);
  };
  
  const handleSubmitSignature = async () => {
    if (!signature_id || !signatureData) {
      return;
    }
    
    try {
      setSubmitting(true);
      console.log(`Submitting signature for signature ID: ${signature_id}`);
      
      const result = await submitSignatureById(signature_id, signatureData);
      console.log('Signature submission result:', result);
      
      setSignatureSubmitted(true);
      
      // Navigate to success page
      navigate('/signature-success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('Error submitting signature:', errorMessage);
      setError(`Failed to submit signature: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Agreement...</h2>
          <p className="text-gray-500 mt-2">Please wait while we retrieve your agreement.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Agreement</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            {debugInfo && (
              <div className="mt-4 p-4 bg-gray-100 rounded-lg text-left text-xs text-gray-700 font-mono overflow-auto max-h-40">
                <pre>{debugInfo}</pre>
              </div>
            )}
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Return Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (agreementId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Signature for {signerName}
            </h1>
            <p className="text-gray-600">
              Please review and sign the agreement below
            </p>
          </div>

          {/* Render the AgreementPreview component with secondary signer props */}
          <AgreementPreview
            onBack={handleBack}
            isSecondaryView={true}
            signatureId={signature_id}
            secondarySignerProps={{
              signerName,
              signatureData,
              signatureSubmitted,
              submitting,
              onSignatureChange: handleSignatureChange,
              onSubmitSignature: handleSubmitSignature
            }}
          />
        </div>
      </div>
    );
  }

  return null;
}